import {
  useEffect,
  useId,
  useRef,
  useState,
  type HTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

import { Button } from "./Button";
import { useEscapeDismiss } from "./layerUtils";
import { ShellIcon, type ShellIconName } from "./Shell";

type MenuAlign = "start" | "end";

export interface MenuItem {
  /** Renders in the error color. §4a law: callers put the danger item LAST. */
  danger?: boolean;
  /** Rendered with `aria-disabled`; skipped by arrow navigation, never selects. */
  disabled?: boolean;
  /** Optional leading icon for Menu items. The text label always stays. */
  icon?: ShellIconName;
  id: string;
  label: string;
  onSelect: () => void;
}

function classes(base: string[], className: string | undefined): string {
  if (className) {
    base.push(className);
  }

  return base.join(" ");
}

export interface MenuProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> {
  align?: MenuAlign;
  items: MenuItem[];
  label: string;
  /**
   * "button" (default) renders the text-label secondary Button trigger.
   * "kebab" renders the icon-only `⋯` trigger sanctioned for repeated data
   * tiles (§2.12 exception); `label` becomes its aria-label.
   */
  trigger?: "button" | "kebab";
}

/**
 * Menu — the UI-LAW §4a "Menu" layer: 3+ related page/row actions that would
 * otherwise widen an action row. A `danger` item renders in the error color
 * and, per §4a, callers place it last — that ordering is law, not enforced
 * here. The default text trigger is a secondary Button; the open surface is
 * the popover plane (hairline border, surface background, z-index 70)
 * anchored below the trigger.
 *
 * Keyboard contract: open focuses the first enabled item; ArrowUp/ArrowDown
 * move with wrap (skipping disabled items); Home/End jump to the first/last
 * enabled item; Enter/Space select then close and return focus to the
 * trigger; Escape closes and returns focus; outside mousedown closes.
 */
export function Menu({
  align = "end",
  className,
  items,
  label,
  trigger = "button",
  ...props
}: MenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const wasOpenRef = useRef(false);
  const menuId = useId();
  const triggerId = useId();

  const closeAndReturnFocus = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  useEscapeDismiss(closeAndReturnFocus, isOpen);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (surfaceRef.current?.contains(target)) {
        return;
      }
      if (triggerRef.current?.contains(target)) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener("mousedown", onMouseDown, true);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      const firstEnabledIndex = items.findIndex((item) => !item.disabled);
      if (firstEnabledIndex >= 0) {
        itemRefs.current[firstEnabledIndex]?.focus();
      }
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, items]);

  const enabledIndexes = () =>
    items.flatMap((item, index) => (item.disabled ? [] : [index]));

  const focusedIndex = () =>
    itemRefs.current.findIndex(
      (element) => element !== null && element === document.activeElement,
    );

  const focusItemAt = (index: number | undefined) => {
    if (index !== undefined) {
      itemRefs.current[index]?.focus();
    }
  };

  const moveFocus = (direction: 1 | -1) => {
    const enabled = enabledIndexes();
    if (enabled.length === 0) {
      return;
    }
    const currentPosition = enabled.indexOf(focusedIndex());
    if (currentPosition === -1) {
      focusItemAt(direction === 1 ? enabled[0] : enabled[enabled.length - 1]);
      return;
    }
    const nextPosition =
      (currentPosition + direction + enabled.length) % enabled.length;
    focusItemAt(enabled[nextPosition]);
  };

  const selectItem = (item: MenuItem) => {
    if (item.disabled) {
      return;
    }
    item.onSelect();
    closeAndReturnFocus();
  };

  const onSurfaceKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        moveFocus(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        moveFocus(-1);
        break;
      case "Home": {
        event.preventDefault();
        focusItemAt(enabledIndexes()[0]);
        break;
      }
      case "End": {
        event.preventDefault();
        const enabled = enabledIndexes();
        focusItemAt(enabled[enabled.length - 1]);
        break;
      }
      case "Enter":
      case " ": {
        event.preventDefault();
        const index = focusedIndex();
        const item = index >= 0 ? items[index] : undefined;
        if (item !== undefined) {
          selectItem(item);
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <div className={classes(["marker-menu-anchor"], className)} {...props}>
      {trigger === "kebab" ? (
        <button
          aria-controls={isOpen ? menuId : undefined}
          aria-expanded={isOpen}
          aria-haspopup="menu"
          aria-label={label}
          className="marker-menu__kebab"
          id={triggerId}
          ref={triggerRef}
          type="button"
          onClick={() => {
            setIsOpen((current) => !current);
          }}
        >
          <ShellIcon name="ellipsis" />
        </button>
      ) : (
        <Button
          aria-controls={isOpen ? menuId : undefined}
          aria-expanded={isOpen}
          aria-haspopup="menu"
          id={triggerId}
          ref={triggerRef}
          size="sm"
          variant="secondary"
          onClick={() => {
            setIsOpen((current) => !current);
          }}
        >
          {label}
        </Button>
      )}
      {isOpen ? (
        <div
          aria-labelledby={triggerId}
          className={["marker-menu", `marker-menu--${align}`].join(" ")}
          id={menuId}
          onKeyDown={onSurfaceKeyDown}
          ref={surfaceRef}
          role="menu"
        >
          {items.map((item, index) => (
            <button
              aria-disabled={item.disabled ? true : undefined}
              className={classes(
                ["marker-menu__item"],
                item.danger ? "marker-menu__item--danger" : undefined,
              )}
              key={item.id}
              onClick={() => {
                selectItem(item);
              }}
              ref={(element) => {
                itemRefs.current[index] = element;
              }}
              role="menuitem"
              tabIndex={-1}
              type="button"
            >
              {item.icon ? (
                <span aria-hidden="true" className="marker-menu__item-icon">
                  <ShellIcon name={item.icon} />
                </span>
              ) : null}
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
